import React from "react";
import { motion } from "framer-motion";
import { BedDouble, Bath, Users, Waves, Ruler, Star, ArrowRight } from "lucide-react";
import { useBooking, VILLA, VILLA_PHOTOS } from "../data";

const FACTS = [
    { icon: BedDouble, label: "Bedrooms", value: "3" },
    { icon: Bath, label: "Bathrooms", value: "2.5" },
    { icon: Users, label: "Sleeps", value: "6" },
    { icon: Ruler, label: "Interior", value: "1,609 sq ft" },
    { icon: Waves, label: "To the sand", value: "3 min" },
    { icon: Star, label: "Guest rating", value: "10 / 10" },
];

export default function Overview() {
    const { openBooking } = useBooking();
    return (
        <section id="overview" className="relative py-24 lg:py-36 bg-[#FAF8F5]" data-testid="overview-section">
            <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
                <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}>
                    <p className="text-xs uppercase tracking-[0.3em] text-[#C49A45] font-semibold" data-testid="overview-eyebrow">The featured stay</p>
                    <h2 className="font-serif-d font-light text-4xl sm:text-5xl lg:text-6xl text-[#0F2837] mt-4 leading-[1.05]">
                        Your Amelia Island <em className="text-[#2D6A76]">home base.</em>
                    </h2>
                    <p className="mt-6 text-[#5C6A78] leading-relaxed max-w-xl">
                        Enjoy the entire home with only your travelling party. {VILLA.name} {VILLA.unit} pairs a private balcony and community pool with a full kitchen, in-unit laundry and fast Wi-Fi — moments from Fernandina Beach, Main Beach Park and Fort Clinch State Park.
                    </p>
                    <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="overview-facts">
                        {FACTS.map(({ icon: Icon, label, value }) => (
                            <div key={label} className="rounded-2xl border border-[#E5DCCB] bg-white px-4 py-4 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300">
                                <Icon size={18} className="text-[#C49A45]" />
                                <p className="mt-2 font-serif-d text-2xl text-[#0F2837]">{value}</p>
                                <p className="text-[11px] uppercase tracking-[0.15em] text-[#5C6A78]">{label}</p>
                            </div>
                        ))}
                    </div>
                    <button onClick={() => openBooking()} data-testid="overview-book-button"
                        className="btn-primary-lux mt-9 inline-flex items-center gap-2 rounded-full px-8 py-4 text-sm font-semibold tracking-wide">
                        Book a Stay <ArrowRight size={16} />
                    </button>
                </motion.div>

                <motion.div initial={{ opacity: 0, scale: 0.96 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                    className="relative">
                    <div className="rounded-[2rem] overflow-hidden shadow-[0_40px_80px_-30px_rgba(15,40,55,0.45)]">
                        <img src={VILLA_PHOTOS.living} alt="Living room of Fernandina Shores #6322" className="w-full h-[420px] lg:h-[540px] object-cover" loading="lazy" />
                    </div>
                    <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.3, duration: 0.8 }}
                        className="absolute -bottom-8 -left-4 sm:-left-8 w-44 sm:w-56 rounded-2xl overflow-hidden border-4 border-[#FAF8F5] shadow-xl rotate-[-3deg]">
                        <img src={VILLA_PHOTOS.blueBedroom} alt="Coastal bedroom with sea-blue walls" className="w-full h-36 sm:h-44 object-cover" loading="lazy" />
                    </motion.div>
                    <div className="absolute -top-5 -right-3 sm:-right-6 rounded-2xl bg-[#0F2837] text-white px-5 py-4 shadow-xl rotate-[2deg]">
                        <p className="text-[10px] uppercase tracking-[0.25em] text-[#C49A45]">From</p>
                        <p className="font-serif-d text-3xl">$624<span className="text-sm text-white/60"> /night</span></p>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
